/**
 * Hexagon Data Transform
 *
 * Pure transformation layer applied to raw hexagon API responses.
 * Enriches hexagon metadata with computed stem estimation fields
 * without mutating the original data.
 *
 * Designed to be used with React Query's `select` option so the
 * query cache remains immutable while consumers get derived data.
 *
 * @module hexagonTransform
 */

import { enrichStemEstimates } from './stemEnrichment';

/**
 * Enriches a single hexagon object with stem estimation fields.
 * Returns a new object — does not mutate the input.
 *
 * @param {Object} hexagon - A hexagon object with `detections` and `metadata`.
 * @returns {Object} A new hexagon with enriched metadata.
 */
function enrichHexagon(hexagon) {
	if (!hexagon?.metadata) return hexagon;

	const stemFields = enrichStemEstimates(hexagon.detections, hexagon.metadata.area);

	// If no stem fields were produced, return as-is to preserve referential identity
	if (Object.keys(stemFields).length === 0) return hexagon;

	return {
		...hexagon,
		metadata: {
			...hexagon.metadata,
			...stemFields,
		},
	};
}

/**
 * Transforms the raw hexagon API response by enriching all hexagons
 * and the total summary with computed stem estimation fields.
 *
 * Pure function — does not mutate the input. Suitable for use as a
 * React Query `select` function.
 *
 * @param {Object|null} data - Raw response from getHexagonFeatures.
 * @returns {Object|null} Transformed data with enriched metadata.
 */
export function enrichHexagonData(data) {
	if (!data?.hexagon_data) return data;

	// Enrich the total summary metadata
	const enrichedTotal = data.metadata?.total
		? enrichHexagon(data.metadata.total)
		: data.metadata?.total;

	// Enrich each individual hexagon
	const enrichedHexagonData = {};
	for (const [key, hexagon] of Object.entries(data.hexagon_data)) {
		enrichedHexagonData[key] = enrichHexagon(hexagon);
	}

	return {
		...data,
		metadata: { ...data.metadata, total: enrichedTotal },
		hexagon_data: enrichedHexagonData,
	};
}
